import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import * as JWTService from '../utils/JWTService';

// Define a service using a base URL and expected endpoints
//See https://redux-toolkit.js.org/tutorials/rtk-query/
export const featureflowApi = createApi({
    reducerPath: 'featureflowApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '/api/', prepareHeaders: (headers, {getState}) => {
            //const token = getState().auth.token
            const token = JWTService.getAuthToken();
            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }
            return headers
        },
    }),
    tagTypes: ['experiments', 'features', 'projects', 'segments', 'products'],
    endpoints: (build) => ({

        ////projects
        getProjectByKey: build.query({
            query: (key) => `v1/projects/${key}`,
            // providesTags: ({id})  => ({ type: 'projects', id }),
        }),
        getProjects: build.query({
            query: () => `v1/projects/`,
            providesTags: (result) =>
                result
                    ?
                    [
                        ...result.map(({id}) => ({type: 'projects', id})), //great a unique index
                        {type: 'projects', id: 'LIST'} //plus a LIST
                    ]
                    :
                    [{type: 'projects', id: 'LIST'}],
        }),

        ////environments
        getEnvironmentByProjectKeyAndEnvironmentKey: build.query({
            query: (options: { projectKey: string, environmentKey: string }) => `v1/environments/${options.projectKey}/${options.environmentKey}`,
        }),

        ////experiments
        getExperiment: build.query({
            query: (id) => `v1/experiments/${id}`,
            providesTags: ({id}) => [{type: 'experiments', id}],
        }),
        getExperiments: build.query({
            query: () => `v1/experiments/`,
            providesTags: (result) => [{type: 'experiments', id: 'LIST'}],
        }),
        //billing products
        getProducts: build.query({
            query: () => `v1/products/`,
            providesTags: (result) => [{type: 'products', id: 'LIST'}],
        }),
        ////features
        getFeaturesByProjectKey: build.query({
            query: (options: {projectKey: string, query: ?string}) => `v1/projects/${options.projectKey}/features?query=${options.query||''}`,
            providesTags: (result) =>
                result
                    ?
                    [
                        ...result.map(({id}) => ({type: 'features', id})), //great a unique index
                        {type: 'features', id: 'LIST'} //plus a LIST
                    ]
                    :
                    [{type: 'features', id: 'LIST'}],
        }),
        getFeatureByProjectKeyAndFeatureKey: build.query({
            query: (options: { projectKey: string, featureKey: string }) => `v1/projects/${options.projectKey}/features/${options.featureKey}`,
            //query: (options: {projectKey: string, featureKey: string}) => `/features/${options.projectKey}/${options.featureKey}`,
            providesTags: (result, error, arg) => {
                return result ? [({type: 'features', id: result.id}), 'features']
                    : [{type: 'features', id: 'LIST'}]
            }

            //TBC query: (options: {projectKey: string, featureKey: string}) => `features/${options.projectKey}/${options.featureKey}`,
        }),
        ///Segments
        getSegments: build.query({
            query: (options: { projectKey: string, environmentKey: string, query?: string }) => `v2/segments/${options.projectKey}/${options.environmentKey}?query=${options.query}`,
        }),

        ////mutations
        updateFeature: build.mutation({
            query: ({projectKey, id, ...patch}) => ({
                url: `v1/projects/${projectKey}/features`,
                method: 'PUT',
                body: patch
            }),
            invalidatesTags: (result, error, {id}) => {
                return [{type: 'features', id}]
            }
        }),
        createFeature: build.mutation({
            query: ({projectKey, ...body}) => ({
                url: `v1/projects/${projectKey}/features`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: (result, error) => {
                return [
                    {type: 'features', id: 'LIST'},
                    {type: 'features', id: result.id},
                ]
            },
            // async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) { //optimistic update
            //   const patchResult = dispatch(
            //     featureflowApi.util.updateQueryData('getFeatureByProjectKeyAndFeatureKey', {projectKey: patch.projectKey, featureKey: patch.key}, (draft) => {
            //       Object.assign(draft, patch)
            //     })
            //   )
            //   try {
            //     await queryFulfilled
            //   } catch {
            //     patchResult.undo()
            //   }
            // },

        }),
        linkFeature: build.mutation({
            query: ({projectKey, ...patch}) => ({
                url: `v1/features/link`,
                method: 'PUT',
                body: patch
            })
        }),
        updateBookmark: build.mutation({
            query: ({featureId, bookmarked}) => ({
                url: `v1/features/${featureId}/bookmark/${bookmarked}`,
                method: 'PUT',
            }),
            invalidatesTags: (result, error) => [{type: 'features', id: result.id}]
        }),
        deleteFeature: build.mutation({
            query: ({projectKey, featureId}) => ({
                url: `v1/project/${projectKey}/features/${featureId}/`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, {featureId}) => [{type: 'features', featureId}]
        }),
        updateFeatureControl: build.mutation({
            query(body) {
                return {
                    url: `v1/feature-controls/`,
                    method: 'PUT',
                    body
                }
            },
            invalidatesTags: (result, error, {featureId}) => [{type: 'features', featureId}]
        }),
        updateEnvironment: build.mutation({
            query(body) {
                return {
                    url: `v1/environments/`,
                    method: body.id ? 'PUT' : 'POST',
                    body
                }
            },
            invalidatesTags: (result, error, {projectId}) => [{type: 'projects', projectId}]
        }),
        deleteEnvironment: build.mutation({
            query: (id) => ({
                url: `v1/environments/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, {id}) => [{type: 'projects', id: 'LIST'}]
        }),
        updateExperiment: build.mutation({
            query(body) {
                return {
                    url: `v1/experiments/`,
                    method: body.id ? 'PUT' : 'POST',
                    body
                }
            },
            invalidatesTags: (result, error, {id}) => {
                return [{type: 'experiments', id: 'LIST'}, {type: 'experiments', id}]
            }
        }),
        deleteExperiment: build.mutation({
            query: (id) => ({
                url: `v1/experiments/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, {id}) => [{type: 'experiments', id: 'LIST'}]
        }),
        updateProject: build.mutation({
            query(body) {
                return {
                    url: `v1/projects/`,
                    method: body.id ? 'PUT' : 'POST',
                    body
                }
            },
            invalidatesTags: (result, error, {id}) => [{type: 'projects', id}]
        }),
        deleteProject: build.mutation({
            query: (key) => ({
                url: `v1/projects/${key}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, {id}) => [{type: 'projects', id: 'LIST'}] //
        }),
        updateSegment: build.mutation({
            query(body) {
                return {
                    url: `v2/segments/`,
                    method: body.id ? 'PUT' : 'POST',
                    body
                }
            },
            invalidatesTags: (result, error) => {
                return [
                    {type: 'segments', id: 'LIST'},
                    {type: 'segments', id: result.id},
                ]
            },
        }),
    })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    //Queries
    useGetEnvironmentByProjectKeyAndEnvironmentKeyQuery,
    useGetFeatureByProjectKeyAndFeatureKeyQuery,
    useGetFeaturesByProjectKeyQuery,
    useGetProjectsQuery,
    useGetProjectByKeyQuery,
    useGetExperimentQuery,
    useGetExperimentsQuery,
    //billing
    useGetProductsQuery,
    //segments
    useGetSegmentsQuery,
    useUpdateSegmentMutation,
    //mutations
    //features
    useCreateFeatureMutation,
    useUpdateFeatureMutation,
    useLinkFeatureMutation,
    useUpdateBookmarkMutation,
    useDeleteFeatureMutation,
    //feature-controls
    useUpdateFeatureControlMutation,
    //environments
    useUpdateEnvironmentMutation,
    useDeleteEnvironmentMutation,
    //experiments
    useUpdateExperimentMutation,
    useDeleteExperimentMutation,
    //projects
    useUpdateProjectMutation,
    useDeleteProjectMutation,

} = featureflowApi;
